import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import {
  countActiveP2P,
  countActiveTips,
  countLivePlayGames,
  countOpenHouses,
  countOpenPredictMarkets,
} from "../utils/workspaceNavCounts";

const authHeaders = () => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.bets)) return payload.bets;
  if (Array.isArray(payload?.predictions)) return payload.predictions;
  if (Array.isArray(payload?.markets)) return payload.markets;
  if (Array.isArray(payload?.games)) return payload.games;
  return [];
};

export const refreshWorkspaceNavCounts = createAsyncThunk(
  "workspaceNav/refresh",
  async (_, { rejectWithValue }) => {
    try {
      const [
        housesRes,
        p2pRes,
        marketsRes,
        wheelRes,
        casinoRes,
        communityRes,
        aiRes,
      ] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/bets/published`, {
          params: { page: 1, limit: 100 },
        }),
        axios.get(`${API_BASE_URL}/api/p2p`, { params: { page: 1, limit: 100 } }),
        axios.get(`${API_BASE_URL}/api/polymarket-v2/markets`, {
          params: { status: "OPEN", limit: 100, skip: 0 },
        }),
        axios.get(`${API_BASE_URL}/api/bondWheel/games`, { params: { limit: 100 } }),
        axios.get(`${API_BASE_URL}/api/casino`, { params: { page: 1, limit: 100 } }),
        axios.get(`${API_BASE_URL}/api/predictions`, {
          params: { page: 1, limit: 100 },
        }),
        axios.get(`${API_BASE_URL}/api/predictions/ai`, {
          params: { page: 1, limit: 100 },
          headers: authHeaders(),
        }),
      ]);

      const houses =
        housesRes.status === "fulfilled" ? unwrapList(housesRes.value.data) : [];
      const p2p = p2pRes.status === "fulfilled" ? unwrapList(p2pRes.value.data) : [];
      const marketsPayload =
        marketsRes.status === "fulfilled" ? marketsRes.value.data : {};
      const markets = unwrapList(marketsPayload);
      const wheel =
        wheelRes.status === "fulfilled" ? unwrapList(wheelRes.value.data) : [];
      const casinoPayload =
        casinoRes.status === "fulfilled" ? casinoRes.value.data : {};
      const casino = unwrapList(casinoPayload);
      const community =
        communityRes.status === "fulfilled" ? unwrapList(communityRes.value.data) : [];
      const ai = aiRes.status === "fulfilled" ? unwrapList(aiRes.value.data) : [];

      return {
        house: countOpenHouses(houses),
        p2p: countActiveP2P(p2p),
        predict: countOpenPredictMarkets(markets, marketsPayload?.pagination),
        play: countLivePlayGames(wheel, casino),
        tips: countActiveTips(community, ai),
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load nav counts");
    }
  }
);

const initialState = {
  counts: {
    house: 0,
    p2p: 0,
    predict: 0,
    play: 0,
    tips: 0,
  },
  status: "idle",
  lastUpdated: null,
};

const workspaceNavSlice = createSlice({
  name: "workspaceNav",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(refreshWorkspaceNavCounts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(refreshWorkspaceNavCounts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.counts = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(refreshWorkspaceNavCounts.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const selectWorkspaceNavCounts = (state) =>
  state.workspaceNav?.counts || initialState.counts;

export default workspaceNavSlice.reducer;
